import type { TerrainSource } from '../calc/terrain.js'
import type { Activity } from '../model/activity.js'
import { applyOperation, type Operation, type OperationWarning } from './operations.js'

/**
 * Operation-log undo/redo (`edit-history` spec). The current activity is
 * always derived by replaying `log[0, cursor)` over `original` — never
 * computed by mutating a running activity in place — so every function
 * here is a pure `(state) => state` transform and `original` is never
 * touched after `createHistory`.
 */
export interface EditHistoryState {
  readonly original: Activity
  readonly log: readonly Operation[]
  readonly cursor: number
}

export function createHistory(original: Activity): EditHistoryState {
  return { original, log: [], cursor: 0 }
}

export interface ReplayResult {
  activity: Activity
  warnings: OperationWarning[]
}

function replay(original: Activity, log: readonly Operation[], cursor: number): ReplayResult {
  let activity = original
  const warnings: OperationWarning[] = []
  for (let i = 0; i < cursor; i++) {
    const operation = log[i]
    if (operation == null) continue
    const result = applyOperation(activity, operation)
    activity = result.activity
    warnings.push(...result.warnings)
  }
  return { activity, warnings }
}

/** The activity state as of `state.cursor` — i.e. after undo/redo are applied. */
export function currentActivity(state: EditHistoryState): ReplayResult {
  return replay(state.original, state.log, state.cursor)
}

/**
 * Applies a new operation on top of the current state. If the cursor is
 * before the end of the log (the user had undone something), the
 * now-orphaned forward history is discarded — per spec, this is the one
 * case where history entries are actually removed, not just the cursor
 * moved.
 */
export function applyToHistory(state: EditHistoryState, operation: Operation): EditHistoryState {
  const truncatedLog = state.log.slice(0, state.cursor)
  const newLog = [...truncatedLog, operation]
  return { original: state.original, log: newLog, cursor: newLog.length }
}

export function undo(state: EditHistoryState): EditHistoryState {
  if (state.cursor === 0) return state
  return { ...state, cursor: state.cursor - 1 }
}

export function redo(state: EditHistoryState): EditHistoryState {
  if (state.cursor >= state.log.length) return state
  return { ...state, cursor: state.cursor + 1 }
}

export function canUndo(state: EditHistoryState): boolean {
  return state.cursor > 0
}

export function canRedo(state: EditHistoryState): boolean {
  return state.cursor < state.log.length
}

/** Clears all history, returning exactly the original decoded snapshot. */
export function resetToOriginal(state: EditHistoryState): EditHistoryState {
  return { original: state.original, log: [], cursor: 0 }
}

/**
 * Computes the effect of an operation without committing it to history —
 * the operation log and cursor are completely unchanged. Useful for
 * showing the user a preview before they confirm an edit.
 */
export function previewOperation(state: EditHistoryState, operation: Operation): ReplayResult {
  const { activity, warnings } = currentActivity(state)
  const result = applyOperation(activity, operation)
  return { activity: result.activity, warnings: [...warnings, ...result.warnings] }
}

/**
 * Every *active* (index < cursor) `editField` op, in log order. A
 * hand-edited lap/session value (e.g. `totalCalories`) is neither a
 * device value nor a recalculation, so callers that tag summary values
 * with provenance (e.g. `computeTotalCalories` reports `original`) can
 * use this to relabel or flag fields the user edited.
 */
export function getActiveFieldEdits(
  state: EditHistoryState,
): Array<Extract<Operation, { type: 'editField' }>> {
  const end = Math.min(state.cursor, state.log.length)
  const result: Array<Extract<Operation, { type: 'editField' }>> = []
  for (let i = 0; i < end; i++) {
    const op = state.log[i]
    if (op?.type === 'editField') result.push(op)
  }
  return result
}

/**
 * The `TerrainSource` of the latest *active* (index < cursor)
 * `setElevation` op in the log, or `null` — i.e. whether the current
 * activity's elevation is (at least partly) terrain-derived, so callers
 * can tag ascent/descent `estimated` (`computeAscentDescentMeters`'s
 * `elevationEstimated`) and disclose the source on export. Undone ops
 * (index >= cursor) don't count.
 */
export function getActiveElevationSource(state: EditHistoryState): TerrainSource | null {
  const end = Math.min(state.cursor, state.log.length)
  for (let i = end - 1; i >= 0; i--) {
    const op = state.log[i]
    if (op?.type === 'setElevation') return op.source
  }
  return null
}
